
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ChatBotProvider, useChatBotContext } from './context/ChatBotContext';
import { ChatBotButton } from './components/ChatBotButton';
import { ChatBotWindow } from './components/ChatBotWindow';
import {chatBotService } from "./services/ChatBotService";
import { RootState } from '../../reducers';
import { ObjectUtils, USER_TOKEN } from '../../utils';
import './styles/chatbot.scss';

const getEmployeeKey = (token?: string | null): string => {
  try{
    const payload = ObjectUtils.parseJwt(token) as { sub?: string; unique_name?: string } | undefined;
    return payload?.sub || payload?.unique_name || 'anonymous';
  }catch{
    return 'anonymous';
  }
};

const ChatBotWidgetContent: React.FC = () => {
  const {
    state,
    toggleChat,
    assistantName,
    chatWindowTitle,
    labels: localizedLabels,
    isChatbotEnabled,
    isInitLoaded,
  } = useChatBotContext();
  const launcherRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(state.isOpen);

  // Closing the panel must not drop focus to <body>
  useEffect(() => {
    if (wasOpenRef.current && !state.isOpen) {
      launcherRef.current?.focus();
    }
    wasOpenRef.current = state.isOpen;
  }, [state.isOpen]);

  if (!isInitLoaded || !isChatbotEnabled) {
    return null;
  }

  return (
    <>
      <ChatBotButton
        ref={launcherRef}
        onClick={toggleChat}
        unreadCount={state.unreadCount}
        assistantName={assistantName}
        openChatLabel={localizedLabels.openChat}
        openChatWithNameTemplate={localizedLabels.openChatWithNameTemplate}
        isOpen={state.isOpen}
      />
      <ChatBotWindow assistantName={chatWindowTitle} labels={localizedLabels} />
    </>
  );
};

export const ChatBotWidget: React.FC = () => {
  const location = useLocation();
  const path = location.pathname?.toLowerCase() ||"";
  const auth = useSelector((state: RootState) => state.auth);

  const tokenState = USER_TOKEN.get();
  const chatNotAllowedPage = path.endsWith('/login') || path.endsWith('/logoff') || path.endsWith('/changepassword');
  const isChatAuthenticated = (!!tokenState.isSignedIn || !!auth?.isSignedIn ) && !chatNotAllowedPage && !auth?.needTwoFAValidation

  const employeeKey = getEmployeeKey(tokenState.token);

  useEffect(() => {
    if (!isChatAuthenticated) {
      chatBotService.clearAllChatBotDataService();
    }
  }, [isChatAuthenticated]);

  if (!isChatAuthenticated) {
    return null;
  }

  return (
    <ChatBotProvider key={employeeKey}>
      <ChatBotWidgetContent />
    </ChatBotProvider>
  );
};

export default ChatBotWidget;
